import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useTypedSelector } from 'src/State/RootReducer'
import { generateReferralCode, getUserReferralCodeStats } from '../apis'
import { getReferralStatsValue } from '../utils'
import { useUser } from 'src/Hooks/useUser'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'

type UseGenerateReferralCodeReturnType = {
  handleGenerateReferralCode: () => void
  referallsAvailble: number
  isGenerating: boolean
}

/**
 * This hook is responsible of sending a signal to create referral code and execute something when the request success or failed
 */
export const useGenerateReferralCode = (): UseGenerateReferralCodeReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { contactData } = useUser()
  const pageName = useTypedSelector(state => state.app.title)

  const { data: statsData, refetch: updateReferralCounter } = usePortalQuery({
    queryKey: ['userReferralCodeStats'],
    queryFn: () => getUserReferralCodeStats(authFetch),
  })

  const { mutate, isPending: isGenerating } = usePortalMutation({
    mutationFn: () => generateReferralCode(authFetch),
    onSuccess: () => {
      enqueueSuccessStack(t('referral.generated.success', { pageName }))
      updateReferralCounter()
    },
    onError: () => {
      enqueueErrorStack(t('referral.generated.error', { pageName }))
    },
  })

  const referallsAvailble = getReferralStatsValue(statsData?.referralCounter ?? 0)

  const handleGenerateReferralCode = (): void => {
    if (!contactData?.Email) {
      enqueueErrorStack(t('referral.generated.no-email-error', { pageName }))
      return
    }
    mutate()
  }

  return {
    handleGenerateReferralCode,
    referallsAvailble,
    isGenerating,
  }
}
